import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ScrollText, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { VerificationBadge } from "@/components/VerificationBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CRED_LABELS = { DARPAN: "DARPAN", CSR1: "CSR-1", "12A": "12A", "80G": "80G", "10_23C": "10(23C)", FCRA: "FCRA" };

export default function AdminConsole() {
  const [orgs, setOrgs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [active, setActive] = useState(null);
  const [tier, setTier] = useState(1);
  const [verifier, setVerifier] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/queue").then((r) => setOrgs(r.data.orgs || []));
    api.get("/admin/audit-log").then((r) => setLogs(r.data.logs || []));
    api.get("/enquiries").then((r) => setEnquiries(r.data.enquiries || [])).catch(() => {});
  };
  useEffect(load, []);

  const open = (o) => { setActive(o); setTier(o.verification_tier || 1); setVerifier(""); setNote(""); };

  const verify = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/orgs/${active.id}/verify`, { tier, note, verifier_name: verifier });
      toast.success(`${active.display_name} set to Tier ${tier}`);
      setActive(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const pending = orgs.filter((o) => o.status !== "published");
  const published = orgs.filter((o) => o.status === "published");

  const Table = ({ items }) => (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-brand text-white"><tr>
          <th className="text-left p-3">Organisation</th><th className="text-left p-3">District</th>
          <th className="text-left p-3">Credentials</th><th className="text-left p-3">Tier</th><th className="p-3"></th>
        </tr></thead>
        <tbody>
          {items.map((o) => (
            <tr key={o.id} className="border-t border-stone-100" data-testid={`admin-row-${o.id}`}>
              <td className="p-3 font-medium text-stone-800">{o.display_name}<div className="text-xs text-stone-400 font-normal">{o.reg_type} · {o.darpan_id}</div></td>
              <td className="p-3 text-stone-600">{o.district}</td>
              <td className="p-3 text-xs text-stone-500">{(o.credentials || []).map((c) => CRED_LABELS[c.type] || c.type).join(", ") || "—"}</td>
              <td className="p-3"><VerificationBadge tier={o.verification_tier} /></td>
              <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => open(o)} data-testid={`verify-btn-${o.id}`}>Review</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-brand flex items-center gap-2"><ShieldCheck size={26} className="text-terracotta" /> Verification console</h1>
      <p className="text-sm text-stone-500">Assign tiers, review documents, and keep an immutable audit trail.</p>

      <Tabs defaultValue="pending" className="mt-6">
        <TabsList>
          <TabsTrigger value="pending" data-testid="admin-tab-pending">Pending queue ({pending.length})</TabsTrigger>
          <TabsTrigger value="published" data-testid="admin-tab-published">Published ({published.length})</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit log</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-5">{pending.length === 0 ? <p className="text-stone-500">Queue is empty.</p> : <Table items={pending} />}</TabsContent>
        <TabsContent value="published" className="mt-5"><Table items={published} /></TabsContent>
        <TabsContent value="audit" className="mt-5">
          <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
            {logs.length === 0 ? <p className="p-4 text-stone-500 flex items-center gap-2"><ScrollText size={16} /> No actions logged yet.</p> : logs.map((l) => (
              <div key={l.id} className="p-3 text-sm flex items-center justify-between" data-testid={`audit-${l.id}`}>
                <span><strong>{l.actor_email}</strong> · {l.action} → Tier {l.after_json?.verification_tier}{l.note ? ` (${l.note})` : ""}</span>
                <span className="text-stone-400 text-xs">{new Date(l.occurred_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="enquiries" className="mt-5">
          <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
            {enquiries.length === 0 ? <p className="p-4 text-stone-500">No enquiries yet.</p> : enquiries.map((e) => (
              <div key={e.id} className="p-4 text-sm" data-testid={`enquiry-${e.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-brand">{e.name} <span className="text-xs font-normal text-stone-500">· {e.org_type}</span></span>
                  <span className="text-stone-400 text-xs">{new Date(e.created_at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-stone-500 mt-0.5">{e.email}{e.phone ? ` · ${e.phone}` : ""}</div>
                <p className="text-stone-700 mt-1">{e.message}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="verify-dialog">
          <DialogHeader><DialogTitle>Verify {active?.display_name}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="text-sm text-stone-600">
                <p className="mb-2">{active.mission}</p>
                <div className="text-xs">Credentials on file: {(active.credentials || []).map((c) => `${CRED_LABELS[c.type] || c.type} ${c.number}`).join(" · ") || "none"}</div>
              </div>
              <div>
                <label className="text-sm font-semibold text-stone-700 block mb-2">Assign verification tier</label>
                <div className="grid grid-cols-4 gap-2">
                  {[0, 1, 2, 3].map((t) => (
                    <button key={t} onClick={() => setTier(t)} data-testid={`tier-option-${t}`}
                      className={`py-2 rounded-lg text-xs font-bold border ${tier === t ? "bg-slate-brand text-white border-slate-brand" : "bg-white text-stone-600 border-stone-200"}`}>Tier {t}</button>
                  ))}
                </div>
              </div>
              {tier >= 3 && <Input placeholder="Verifier name (site visit)" value={verifier} onChange={(e) => setVerifier(e.target.value)} data-testid="verifier-name-input" />}
              <Textarea placeholder="Note (reason / documents checked)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="verify-note-input" />
              <Button onClick={verify} disabled={saving} className="w-full bg-terracotta hover:bg-terracotta-hover" data-testid="confirm-verify-btn">
                {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : null} Publish at Tier {tier}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
